import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../services/auth';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Grid,
  Button,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const CRIT_LABELS = {
  'openrate': "Taux d'Ouverture",
  'clickrate': "Taux de Clics",
  'downloads': "Téléchargements",
  'submissions': "Soumissions de Formulaire"
};

const CRIT_KEYS = ['openrate', 'clickrate', 'downloads', 'submissions'];

const AbTestResults = ({ parentEmailId, raw = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailName = location.state?.emailName;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = raw
          ? `/abtest_notify/results_raw/${parentEmailId}`
          : `/abtest_notify/results/${parentEmailId}`;
        const response = await api.get(url);
        setData(response.data);
      } catch (e) {
        setError('Erreur lors du chargement des résultats.');
        console.error("A/B test results fetch failed:", e);
      } finally {
        setLoading(false);
      }
    };

    if (parentEmailId) {
      fetchResults();
    }
  }, [parentEmailId, raw]);

  const renderHeader = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <Tooltip title="Retour">
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
      <Typography variant="h4" component="h1">
        Résultats du Test A/B {emailName && <span style={{ color: '#666', fontWeight: 300 }}>: {emailName}</span>}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || data?.error) {
    return (
      <Box sx={{ p: 3 }}>
        {renderHeader()}
        <Alert severity="error">{error || data?.message}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  // Mode RAW : stats brutes de toutes les variantes
  if (raw) {
    const { variations } = data;
    if (!Array.isArray(variations) || variations.length === 0) {
      return (
        <Box sx={{ p: 3 }}>
          {renderHeader()}
          <Alert severity="info">Aucune statistique disponible pour cet email.</Alert>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 3 }}>
        {renderHeader()}
        <Paper elevation={3} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Statistiques (toutes variantes)</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Variation</TableCell>
                  <TableCell>Objet</TableCell>
                  {CRIT_KEYS.map(k => <TableCell key={k} align="right">{CRIT_LABELS[k]}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {variations.map(v => (
                  <TableRow key={v.id}>
                    <TableCell component="th" scope="row">{v.name}</TableCell>
                    <TableCell>{v.subject}</TableCell>
                    <TableCell align="right"><b>{v.openrate?.toFixed(2) || 0}%</b></TableCell>
                    <TableCell align="right"><b>{v.clickrate?.toFixed(2) || 0}%</b></TableCell>
                    <TableCell align="right"><b>{v.downloads || 0}</b></TableCell>
                    <TableCell align="right"><b>{v.submissions || 0}</b></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }

  // Mode normal (avec gagnant, recommandation, etc.)
  const { status, criteria, winner, variations, abtest } = data;
  if (!Array.isArray(variations) || variations.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        {renderHeader()}
        <Alert severity="info">Aucune statistique disponible pour cet email.</Alert>
      </Box>
    );
  }
  
  const critLabel = CRIT_LABELS[criteria] || criteria;
  const winnerVar = variations?.find(v => v.id === winner?.id);
  const improvement = (() => {
    if (!winnerVar || !winnerVar.score) return null;
    const others = variations.filter(v => v.id !== winnerVar.id);
    if (!others.length) return null;
    const maxOther = Math.max(...others.map(v => v.score || 0));
    if (maxOther === 0) return null;
    return Math.round(((winnerVar.score - maxOther) / maxOther) * 100);
  })();

  return (
    <Box sx={{ p: 3 }}>
      {renderHeader()}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2, display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
            <Box textAlign="center"><Typography variant="overline">Statut</Typography><Typography variant="h6">{status}</Typography></Box>
            <Box textAlign="center"><Typography variant="overline">Critère</Typography><Typography variant="h6">{critLabel}</Typography></Box>
            <Box textAlign="center"><Typography variant="overline">Début</Typography><Typography variant="h6">{abtest?.start_date?.slice(0,10)}</Typography></Box>
            <Box textAlign="center"><Typography variant="overline">Fin</Typography><Typography variant="h6">{abtest?.end_date?.slice(0,10)}</Typography></Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Alert severity={winnerVar ? "success" : "info"} icon={<EmojiEventsIcon fontSize="inherit" />}>
            <Typography variant="h6">Analyse & Recommandation</Typography>
            {winnerVar ? (
              <>
                <Typography>Le test est terminé. <b>{winnerVar.name}</b> est la gagnante, avec un {critLabel.toLowerCase()} de <b>{winnerVar.score?.toFixed(2)}%</b>.
                {improvement !== null && improvement > 0 && (
                  <> Cela représente une amélioration de performance d'environ <b>{improvement}%</b>.</>
                )}
                </Typography>
                <Typography sx={{ mt: 1 }}><b>Recommandation :</b> Envoyer la Version Gagnante (<b>{winnerVar.name}</b>) au reste de l'audience.</Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }}>Programmer l'Envoi</Button>
              </>
            ) : <Typography>Aucune variation gagnante détectée pour le moment.</Typography>}
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Comparaison des Performances</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Variation</TableCell>
                    <TableCell>Objet</TableCell>
                    <TableCell align="right">{critLabel}</TableCell>
                    <TableCell align="right">Taux d'Ouverture</TableCell>
                    <TableCell align="right">Taux de Clics</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {variations.map(v => (
                    <TableRow key={v.id} selected={v.id === winnerVar?.id} sx={{ '&.Mui-selected': { backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}>
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {v.id === winnerVar?.id && <EmojiEventsIcon color="success" sx={{ mr: 1 }} />}
                          {v.name}
                        </Box>
                      </TableCell>
                      <TableCell>{v.subject}</TableCell>
                      <TableCell align="right"><b>{v.score?.toFixed(2) || 0}%</b></TableCell>
                      <TableCell align="right">{v.stats?.openrate?.toFixed(2) || '-'}%</TableCell>
                      <TableCell align="right">{v.stats?.clickrate?.toFixed(2) || '-'}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AbTestResults; 